import React, { useEffect, useState, useRef } from 'react';
import { FiCheck, FiX, FiEye, FiEdit, FiTrash2, FiTag, FiArrowLeft, FiArrowRight, FiSave, FiUpload, FiDollarSign, FiCalendar, FiImage, FiTrash } from 'react-icons/fi';
import { getAllProducts, publishProducts, deleteProduct, updateProduct, getProductById, uploadProductImages, deleteProductImage, ApiProduct, deleteMultipleProducts } from '../../api/productApi';

// Form field type for product editing
interface FormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'checkbox' | 'select';
  options?: string[];
  required?: boolean;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'staged' | 'sold'>('all');
  const [viewingProduct, setViewingProduct] = useState<ApiProduct | null>(null);
  const [editingProduct, setEditingProduct] = useState<ApiProduct | null>(null);
  const [formValues, setFormValues] = useState<{[key: string]: any}>({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // State for tracking the current image index in the product view
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // New state for multi-select
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [isMultiDeleteModalOpen, setIsMultiDeleteModalOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);

  // Form fields configuration for product editing
  const formFields: FormField[] = [
    { name: 'brand', label: 'Brand', type: 'text', required: true },
    { name: 'type', label: 'Type', type: 'text', required: true },
    { name: 'collab', label: 'Collaboration', type: 'text' },
    { name: 'colorway', label: 'Colorway', type: 'text', required: true },
    { name: 'size', label: 'Size', type: 'text', required: true },
    { name: 'sizeType', label: 'Size Type', type: 'select', options: ['M', 'W', 'GS', 'TD'], required: true },
    { name: 'condition', label: 'Condition', type: 'text', required: true },
    { name: 'price', label: 'Price', type: 'number', required: true },
    { name: 'ogBox', label: 'Original Box', type: 'checkbox' },
    { name: 'description', label: 'Description', type: 'text' }
  ];

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Function to fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await getAllProducts();
      setProducts(data);
      setError(null);
      
      // Set initial load to false after first successful fetch
      setIsInitialLoad(false);
    } catch (err) {
      setError('Failed to load products. Please try again.');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Show success message with auto-dismiss after 3 seconds
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  // Publish a product that is currently staged
  const handlePublish = async (productId: string) => {
    try {
      const publishedProducts = await publishProducts([productId]);
      
      // Update products in state without full refetch
      setProducts(prevProducts => {
        return prevProducts.map(product => {
          const publishedProduct = publishedProducts.find(p => p.id === product.id);
          return publishedProduct || product;
        });
      });
      
      showSuccess('Product published successfully!');
    } catch (err) {
      setError('Failed to publish product. Please try again.');
      console.error('Error publishing product:', err);
    }
  };

  // Change product status (reactivate sold products, etc.)
  const handleStatusChange = async (productId: string, newStatus: 'active' | 'staged' | 'sold') => {
    try {
      const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update product status');
      }

      const result = await response.json();
      
      // Update products in state
      setProducts(prevProducts => {
        return prevProducts.map(product => {
          if (product.id === productId) {
            return { ...product, status: newStatus };
          }
          return product;
        });
      });
      
      const statusText = newStatus === 'active' ? 'reactivated' : newStatus === 'staged' ? 'moved to staging' : 'marked as sold';
      showSuccess(`Product ${statusText} successfully!`);
    } catch (err) {
      setError(`Failed to update product status. Please try again.`);
      console.error('Error updating product status:', err);
    }
  };

  // View product details
  const handleViewProduct = (product: ApiProduct) => {
    setViewingProduct(product);
    setCurrentImageIndex(0); // Reset image index when viewing a new product
  };

  // Edit product
  const handleEditProduct = (product: ApiProduct) => {
    setEditingProduct(product);
    setFormValues({
      brand: product.brand,
      type: product.type,
      collab: product.collab || '',
      colorway: product.colorway,
      size: product.size,
      sizeType: product.sizeType,
      condition: product.condition,
      price: product.price,
      ogBox: product.ogBox,
      description: product.description || ''
    });
  };

  // Save product changes
  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    
    try {
      // Transform form values to match API expectations (camelCase to snake_case)
      const apiFormValues = {
        brand: formValues.brand,
        type: formValues.type,
        collab: formValues.collab,
        colorway: formValues.colorway,
        size: formValues.size,
        size_type: formValues.sizeType, // Transform sizeType -> size_type
        condition: formValues.condition,
        price: formValues.price,
        og_box: formValues.ogBox, // Transform ogBox -> og_box
        description: formValues.description
      };

      const result = await updateProduct(editingProduct.id, apiFormValues);
      if (result) {
        // API now returns properly formatted data, so we can use it directly
        // Update products in state without full refetch
        setProducts(prevProducts => {
          return prevProducts.map(product => {
            if (product.id === result.id) {
              return result;
            }
            return product;
          });
        });
        
        setEditingProduct(null);
        showSuccess('Product updated successfully!');
      } else {
        setError('Failed to update product. Please try again.');
      }
    } catch (err) {
      setError('Error updating product. Please try again.');
      console.error('Error updating product:', err);
    }
  };

  // Confirm delete product
  const handleConfirmDelete = (productId: string) => {
    setDeleteId(productId);
    setIsDeleting(true);
  };

  // Delete product
  const handleDeleteProduct = async () => {
    if (!deleteId) return;
    
    try {
      setLoading(true);
      console.log(`Attempting to delete product with ID: ${deleteId}`);
      
      const success = await deleteProduct(deleteId);
      
      if (success) {
        console.log(`Product ${deleteId} deleted successfully`);
        
        // Update products in state without full refetch
        setProducts(prevProducts => prevProducts.filter(product => product.id !== deleteId));
        
        // Force a refresh of product data to ensure consistency
        fetchProducts();
        
        setLoading(false);
        setIsDeleting(false);
        setDeleteId(null);
        showSuccess('Product deleted successfully!');
      } else {
        console.error(`Failed to delete product ${deleteId}`);
        setError('Failed to delete product. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Error deleting product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormValues(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };

  // Close view modal
  const closeViewModal = () => {
    setViewingProduct(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditingProduct(null);
  };

  // Cancel delete
  const cancelDelete = () => {
    setIsDeleting(false);
    setDeleteId(null);
  };

  // Filter products based on selected status
  const filteredProducts = selectedStatus === 'all' 
    ? products 
    : products.filter(product => product.status === selectedStatus);

  // Calculate product counts by status
  const productCounts = {
    active: products.filter(p => p.status === 'active').length,
    staged: products.filter(p => p.status === 'staged').length,
    sold: products.filter(p => p.status === 'sold').length,
    total: products.length
  };

  // Pagination logic
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Handle image upload for editing product
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !editingProduct) return;
    
    try {
      setIsUploading(true);
      const files = Array.from(e.target.files);
      
      // Get the position from the file input's data attribute (if replacing)
      const position = e.target.dataset.position ? parseInt(e.target.dataset.position) : undefined;
      
      // Upload the images
      const uploadResult = await uploadProductImages(editingProduct.id, files, position);
      const newImageUrls = uploadResult.urls;
      
      // Update the editing product with new or replaced images
      if (newImageUrls.length > 0) {
        let updatedImages = [...editingProduct.images];
        
        // If replacing, update that specific position
        if (position !== undefined) {
          updatedImages[position] = newImageUrls[0];
        } else {
          // Otherwise append the new images
          updatedImages = [...updatedImages, ...newImageUrls];
        }
        
        const updatedProduct = {
          ...editingProduct,
          images: updatedImages
        };
        
        // Update the editing product state
        setEditingProduct(updatedProduct);
        
        // Also update in the main products list without a full refresh
        setProducts(prevProducts => {
          return prevProducts.map(product => {
            if (product.id === updatedProduct.id) {
              return updatedProduct;
            }
            return product;
          });
        });
        
        if (position !== undefined) {
          showSuccess(`Successfully replaced image at position ${position+1}`);
        } else {
          showSuccess(`Added ${newImageUrls.length} new images`);
        }
        
        // Show warning if some uploads failed
        if (uploadResult.failedUploads && uploadResult.failedUploads.length > 0) {
          setError(`${uploadResult.failedUploads.length} of ${files.length} files failed to upload. Try uploading fewer or smaller files.`);
        }
      }
    } catch (err) {
      setError('Failed to upload images. Please try again.');
      console.error('Error uploading images:', err);
    } finally {
      setIsUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        // Clear the position data attribute
        delete fileInputRef.current.dataset.position;
      }
    }
  };
  
  // Handle replacing a specific image
  const handleReplaceImage = (position: number) => {
    // Set the position data attribute on the file input
    if (fileInputRef.current) {
      fileInputRef.current.dataset.position = position.toString();
      fileInputRef.current.multiple = false; // Only allow one file when replacing
      fileInputRef.current.click();
    }
  };
  
  // Handle deleting a specific image
  const handleDeleteImage = async (position: number) => {
    if (!editingProduct) return;
    
    try {
      // Check if this is the last image - don't allow deletion of all images
      if (editingProduct.images.length <= 1) {
        setError("Cannot delete the last image. Products must have at least one image.");
        return;
      }
      
      // Delete the image
      const updatedProduct = await deleteProductImage(editingProduct.id, position);
      
      if (updatedProduct) {
        // Update the local state
        setEditingProduct(updatedProduct);
        
        // Update the products list without a full refetch
        setProducts(prevProducts => {
          return prevProducts.map(product => {
            if (product.id === updatedProduct.id) {
              return updatedProduct;
            }
            return product;
          });
        });
        
        showSuccess("Image deleted successfully");
      } else {
        setError("Failed to delete image. Please try again.");
      }
    } catch (err) {
      setError('Failed to delete image. Please try again.');
      console.error('Error deleting image:', err);
    }
  };
  
  // Reset file input to normal state after any file operation
  const resetFileInput = () => {
    if (fileInputRef.current) {
      delete fileInputRef.current.dataset.position;
      fileInputRef.current.multiple = true;
      fileInputRef.current.value = '';
    }
  };

  // Navigate to previous image
  const goToPreviousImage = () => {
    if (viewingProduct && viewingProduct.images && viewingProduct.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? viewingProduct.images.length - 1 : prevIndex - 1
      );
    }
  };

  // Navigate to next image
  const goToNextImage = () => {
    if (viewingProduct && viewingProduct.images && viewingProduct.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === viewingProduct.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Select a specific image by index
  const selectImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  // Toggle select mode for multi-select
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      // Clear selection when disabling select mode
      setSelectedProducts([]);
    }
  };

  // Toggle selection of a product
  const toggleProductSelection = (productId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering row click
    
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  // Select/deselect all visible products
  const toggleSelectAll = () => {
    if (selectedProducts.length === currentProducts.length) {
      // If all are selected, deselect all
      setSelectedProducts([]);
    } else {
      // Otherwise select all visible products
      setSelectedProducts(currentProducts.map(product => product.id));
    }
  };

  // Open multi-delete modal
  const openMultiDeleteModal = () => {
    if (selectedProducts.length === 0) {
      setError('No products selected for deletion');
      return;
    }
    setIsMultiDeleteModalOpen(true);
  };

  // Delete multiple products
  const handleDeleteMultipleProducts = async () => {
    if (selectedProducts.length === 0) return;
    
    try {
      setLoading(true);
      console.log(`Attempting to delete ${selectedProducts.length} products`);
      
      const success = await deleteMultipleProducts(selectedProducts);
      
      if (success) {
        console.log(`${selectedProducts.length} products deleted successfully`);
        
        // Update products in state without full refetch
        setProducts(prevProducts => prevProducts.filter(product => !selectedProducts.includes(product.id)));
        
        // Force a refresh of product data to ensure consistency
        fetchProducts();
        
        setLoading(false);
        setIsMultiDeleteModalOpen(false);
        setSelectedProducts([]);
        setSelectMode(false);
        showSuccess(`${selectedProducts.length} products deleted successfully!`);
      } else {
        console.error('Failed to delete multiple products');
        setError('Failed to delete products. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting multiple products:', err);
      setError('Error deleting products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render loading state
  if (loading && isInitialLoad) {
    return (
      <div className="bg-background p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-48 bg-gray-800 animate-pulse rounded"></div>
          <div className="flex space-x-2">
            <div className="h-8 w-16 bg-gray-800 animate-pulse rounded"></div>
            <div className="h-8 w-16 bg-gray-800 animate-pulse rounded"></div>
            <div className="h-8 w-16 bg-gray-800 animate-pulse rounded"></div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-800">
            <thead className="bg-background-light">
              <tr>
                {Array.from({ length: 8 }).map((_, i) => (
                  <th key={i} className="px-6 py-3 text-left">
                    <div className="h-4 w-20 bg-gray-800 animate-pulse rounded"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-gray-800">
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-6 py-4 whitespace-nowrap">
                      <div className="h-10 w-full bg-gray-800 animate-pulse rounded"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  
  // If just refreshing data, use a simpler loading indicator
  const LoadingOverlay = () => loading && !isInitialLoad ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-background p-6 rounded-lg shadow-lg flex flex-col items-center">
        <div className="animate-spin h-12 w-12 border-4 border-accent border-t-transparent rounded-full mb-4"></div>
        <p className="text-primary">Loading products...</p>
      </div>
    </div>
  ) : null;

  return (
    <div className="bg-background p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary mb-3">Products</h2>
          <div className="flex space-x-6">
            <span className="flex items-center text-lg font-semibold">
              <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
              Active Products: {productCounts.active}
            </span>
            <span className="flex items-center text-lg font-semibold">
              <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
              Sold Products: {productCounts.sold}
            </span>
            {productCounts.staged > 0 && (
              <span className="flex items-center text-lg font-semibold">
                <span className="w-3 h-3 bg-amber-500 rounded-full mr-2"></span>
                Staged: {productCounts.staged}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setSelectedStatus('all')}
            className={`px-3 py-1 rounded-md text-sm ${
              selectedStatus === 'all' 
                ? 'bg-accent text-white' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setSelectedStatus('active')}
            className={`px-3 py-1 rounded-md text-sm ${
              selectedStatus === 'active' 
                ? 'bg-green-600 text-white' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setSelectedStatus('staged')}
            className={`px-3 py-1 rounded-md text-sm ${
              selectedStatus === 'staged' 
                ? 'bg-amber-600 text-white' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Staged
          </button>
          <button
            onClick={() => setSelectedStatus('sold')}
            className={`px-3 py-1 rounded-md text-sm ${
              selectedStatus === 'sold' 
                ? 'bg-red-600 text-white' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Sold
          </button>
        </div>
      </div>
      
      {/* Action buttons for bulk operations */}
      {filteredProducts.length > 0 && (
        <div className="flex mb-4 space-x-2">
          <button 
            onClick={toggleSelectMode}
            className={`px-3 py-1 rounded-md text-sm ${
              selectMode 
                ? 'bg-accent text-white' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            {selectMode ? 'Cancel Selection' : 'Select Multiple'}
          </button>
          
          {selectMode && (
            <>
              <button 
                onClick={toggleSelectAll}
                className="px-3 py-1 rounded-md text-sm bg-background-light text-primary hover:bg-gray-800"
              >
                {selectedProducts.length === currentProducts.length ? 'Deselect All' : 'Select All'}
              </button>
              
              <button 
                onClick={openMultiDeleteModal}
                className={`px-3 py-1 rounded-md text-sm flex items-center ${
                  selectedProducts.length > 0
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-600/50 text-white/50 cursor-not-allowed'
                }`}
                disabled={selectedProducts.length === 0}
              >
                <FiTrash className="mr-1 h-3 w-3" />
                Delete Selected ({selectedProducts.length})
              </button>
            </>
          )}
        </div>
      )}
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-4 py-3 rounded-md mb-6 animate-fade-in">
          {successMessage}
        </div>
      )}
      
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-secondary">
          <FiTag className="mx-auto h-12 w-12 mb-4 opacity-30" />
          <p>No products found. Upload some products first.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-6 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <div className="inline-block min-w-full align-middle px-6">
            <div className="overflow-hidden border border-gray-800 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-background-light">
                  <tr>
                    {selectMode && (
                      <th className="pl-4 pr-2 py-3 sticky left-0 bg-background-light z-10">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 text-accent rounded border-gray-700 focus:ring-accent focus:ring-opacity-25 bg-background"
                          checked={selectedProducts.length === currentProducts.length && currentProducts.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Brand</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Collab</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date Added</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Sold Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider sticky right-0 bg-background-light z-10">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-gray-800">
                  {currentProducts.map((product) => (
                    <tr key={product.id} className={selectMode && selectedProducts.includes(product.id) ? "bg-accent/10" : ""}>
                      {selectMode && (
                        <td className="pl-4 pr-2 py-4 whitespace-nowrap sticky left-0 bg-background z-10">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 text-accent rounded border-gray-700 focus:ring-accent focus:ring-opacity-25 bg-background"
                            checked={selectedProducts.includes(product.id)}
                            onChange={(e) => toggleProductSelection(product.id, e as unknown as React.MouseEvent)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className={`flex items-center ${selectMode ? "" : "cursor-pointer hover:bg-background-light p-1 rounded-md transition-colors"}`}
                          onClick={selectMode ? (e) => toggleProductSelection(product.id, e) : () => handleViewProduct(product)}
                        >
                          {product.images && product.images.length > 0 ? (
                            <img 
                              src={product.images[0]} 
                              alt={product.type} 
                              className="h-10 w-10 rounded-md object-cover mr-3"
                              loading="lazy"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                // Fallback if image loading fails
                                (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"><rect width="60" height="60" fill="%23333"/><text x="50%" y="50%" font-family="Arial" font-size="10" fill="white" text-anchor="middle" dominant-baseline="middle">Error</text></svg>';
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-md bg-gray-800 mr-3 flex items-center justify-center">
                              <FiTag className="h-5 w-5 text-gray-500" />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-primary">{product.type}</div>
                            <div className="text-xs text-gray-400">{product.colorway}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                        {product.brand}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {product.collab ? (
                          <span className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
                            {product.collab}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                        {product.size} ({product.sizeType})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                        ${typeof product.price === 'number' ? product.price.toFixed(2) : (product.price || 0)}
                      </td>
                      <td className="px-6 py-4 text-sm text-primary max-w-xs">
                        <div className="truncate" title={product.description || 'No description'}>
                          {product.description ? product.description : <span className="text-gray-500 italic">No description</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          product.status === 'active' 
                            ? 'bg-green-500/10 text-green-400' 
                            : product.status === 'sold'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-amber-500/10 text-amber-400'
                        }`}>
                          {product.status === 'active' ? 'Active' : product.status === 'sold' ? 'Sold' : 'Staged'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {product.createdAt 
                          ? new Date(product.createdAt).toISOString().split('T')[0] 
                          : 'No date'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {product.soldAt 
                          ? new Date(product.soldAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap sticky right-0 bg-background z-10">
                        <div className="flex space-x-2">
                          <button 
                            className="text-blue-400 hover:text-blue-300"
                            title="View details"
                            onClick={() => handleViewProduct(product)}
                          >
                            <FiEye />
                          </button>
                          <button 
                            className="text-accent hover:text-accent/80"
                            title="Edit product"
                            onClick={() => handleEditProduct(product)}
                          >
                            <FiEdit />
                          </button>
                          {product.status === 'staged' && (
                            <button 
                              className="text-green-400 hover:text-green-300"
                              title="Publish product"
                              onClick={() => handlePublish(product.id)}
                            >
                              <FiCheck />
                            </button>
                          )}
                          {product.status === 'sold' && (
                            <button 
                              className="text-green-400 hover:text-green-300"
                              title="Reactivate product"
                              onClick={() => handleStatusChange(product.id, 'active')}
                            >
                              <FiCheck />
                            </button>
                          )}
                          {product.status === 'active' && (
                            <button 
                              className="text-amber-400 hover:text-amber-300"
                              title="Mark as sold"
                              onClick={() => handleStatusChange(product.id, 'sold')}
                            >
                              <FiDollarSign />
                            </button>
                          )}
                          <button 
                            className="text-red-400 hover:text-red-300"
                            title="Delete product"
                            onClick={() => handleConfirmDelete(product.id)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination UI */}
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-400">
            Showing {indexOfFirstProduct + 1} to {Math.min(indexOfLastProduct, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          
          {/* Items per page dropdown */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                const newItemsPerPage = parseInt(e.target.value);
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
              className="bg-background-light border border-gray-700 rounded-md px-2 py-1 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-400">per page</span>
          </div>
        </div>
        
        <div className="flex space-x-1">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === 1 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Previous
          </button>
          
          {pageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => paginate(page)}
              className={`px-3 py-1 rounded-md text-sm ${
                currentPage === page 
                  ? 'bg-accent text-white' 
                  : 'bg-background-light text-primary hover:bg-gray-800'
              }`}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-md text-sm ${
              currentPage === totalPages 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : 'bg-background-light text-primary hover:bg-gray-800'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* View Product Modal */}
      {viewingProduct && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-background border border-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-800">
              <h3 className="text-xl font-semibold text-primary">Product Details</h3>
              <button onClick={closeViewModal} className="text-gray-400 hover:text-gray-300">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3">
                  {viewingProduct.images && viewingProduct.images.length > 0 ? (
                    <div className="relative">
                      <img 
                        src={viewingProduct.images[currentImageIndex]} 
                        alt={viewingProduct.type} 
                        className="w-full h-auto object-cover rounded-lg shadow-lg border border-gray-700 transition-all duration-300" 
                        crossOrigin="anonymous"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="%23333"/><text x="50%" y="50%" font-family="Arial" font-size="20" fill="white" text-anchor="middle" dominant-baseline="middle">Image not available</text></svg>';
                        }}
                      />
                      
                      {viewingProduct.images.length > 1 && (
                        <>
                          {/* Previous arrow */}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              goToPreviousImage();
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-200 w-10 h-10 flex items-center justify-center shadow-lg border border-gray-700"
                            aria-label="Previous image"
                          >
                            <FiArrowLeft className="h-5 w-5" />
                          </button>
                          
                          {/* Next arrow */}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              goToNextImage();
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-200 w-10 h-10 flex items-center justify-center shadow-lg border border-gray-700"
                            aria-label="Next image"
                          >
                            <FiArrowRight className="h-5 w-5" />
                          </button>
                          
                          <div className="grid grid-cols-4 gap-3 mt-4">
                            {viewingProduct.images.map((img, i) => (
                            <img 
                              key={i} 
                              src={img} 
                              alt={`${viewingProduct.type} ${i+1}`} 
                                className={`w-full h-16 object-cover rounded border transition-all duration-200 cursor-pointer 
                                  ${i === currentImageIndex 
                                    ? 'ring-2 ring-accent border-accent shadow-md scale-105' 
                                    : 'opacity-70 hover:opacity-100 border-gray-700 hover:border-gray-500'}`}
                                onClick={() => selectImage(i)}
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"><rect width="60" height="60" fill="%23333"/><text x="50%" y="50%" font-family="Arial" font-size="10" fill="white" text-anchor="middle" dominant-baseline="middle">Error</text></svg>';
                                }}
                            />
                          ))}
                        </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-64 bg-gray-800 rounded-md flex items-center justify-center">
                      <FiTag className="h-12 w-12 text-gray-600" />
                    </div>
                  )}
                </div>
                <div className="md:w-2/3">
                  <h2 className="text-2xl font-bold text-primary">{viewingProduct.brand} {viewingProduct.type}</h2>
                  {viewingProduct.collab && (
                    <div className="mt-1 text-accent">{viewingProduct.collab}</div>
                  )}
                  <div className="mt-1 text-secondary text-lg">{viewingProduct.colorway}</div>
                  <div className="mt-4 text-3xl font-bold text-primary">
                    ${typeof viewingProduct.price === 'number' ? viewingProduct.price.toFixed(2) : parseFloat(viewingProduct.price).toFixed(2)}
                  </div>
                  
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-400">Size</div>
                      <div className="text-primary">{viewingProduct.size} ({viewingProduct.sizeType})</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Condition</div>
                      <div className="text-primary">{viewingProduct.condition}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Original Box</div>
                      <div className="text-primary">{viewingProduct.ogBox ? 'Yes' : 'No'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Status</div>
                      <div className={viewingProduct.status === 'active' ? 'text-green-400' : 'text-amber-400'}>
                        {viewingProduct.status === 'active' ? 'Active' : 'Staged'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-800">
                    <div className="text-sm text-gray-400">Created</div>
                    <div className="text-primary">{viewingProduct.createdAt 
                      ? new Date(viewingProduct.createdAt).toISOString().split('T')[0]
                      : 'No date available'}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Brand</label>
                      <p className="text-white font-medium">{viewingProduct.brand}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Type</label>
                      <p className="text-white font-medium">{viewingProduct.type}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Collaboration</label>
                      <p className="text-white font-medium">{viewingProduct.collab || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Colorway</label>
                      <p className="text-white font-medium">{viewingProduct.colorway}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Size</label>
                      <p className="text-white font-medium">{viewingProduct.size} ({viewingProduct.sizeType})</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Condition</label>
                      <p className="text-white font-medium">{viewingProduct.condition}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Price</label>
                      <p className="text-white font-medium">${typeof viewingProduct.price === 'number' ? viewingProduct.price.toFixed(2) : parseFloat(viewingProduct.price).toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-2">Original Box</label>
                      <p className="text-white font-medium">{viewingProduct.ogBox ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-xs text-gray-400 mb-2">Description</label>
                    <p className="text-white font-medium">{viewingProduct.description || 'No description provided'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-800 flex justify-end">
              <button 
                onClick={closeViewModal}
                className="px-4 py-2 bg-background-light hover:bg-gray-800 rounded-md text-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-background border border-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-800">
              <h3 className="text-xl font-semibold text-primary">Edit Product</h3>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-300">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formFields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-400 ml-1">*</span>}
                    </label>
                    
                    {field.type === 'text' || field.type === 'number' ? (
                      <input
                        type={field.type}
                        name={field.name}
                        value={formValues[field.name] ?? ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-background-light border border-gray-700 rounded-md text-primary focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                        required={field.required}
                        min={field.type === 'number' ? 0 : undefined}
                        step={field.type === 'number' ? 0.01 : undefined}
                      />
                    ) : field.type === 'checkbox' ? (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name={field.name}
                          checked={!!formValues[field.name]}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-accent bg-background-light border border-gray-700 rounded focus:ring-accent"
                        />
                        <span className="ml-2 text-primary">Yes</span>
                      </div>
                    ) : field.type === 'select' ? (
                      <select
                        name={field.name}
                        value={formValues[field.name] ?? ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-background-light border border-gray-700 rounded-md text-primary focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                        required={field.required}
                      >
                        {field.options?.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ))}
              </div>
              
              {/* Product Images Preview */}
              <div className="mt-6 border-t border-gray-800 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-400">Product Images</h4>
                  <div>
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      className="hidden" 
                      accept="image/*"
                      multiple
                    />
                    <button
                      onClick={() => {
                        resetFileInput();
                        fileInputRef.current?.click();
                      }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md flex items-center transition-colors"
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FiUpload className="mr-1.5 h-3 w-3" />
                          Add Multiple Images
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {editingProduct.images && editingProduct.images.length > 0 ? (
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {editingProduct.images.map((img, i) => (
                      <div key={i} className="relative group">
                        <img 
                          src={img} 
                          alt={`Product image ${i+1}`} 
                          className="w-full h-16 object-cover rounded-sm"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"><rect width="60" height="60" fill="%23333"/><text x="50%" y="50%" font-family="Arial" font-size="10" fill="white" text-anchor="middle" dominant-baseline="middle">Error</text></svg>';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="flex space-x-1">
                            <button 
                              onClick={() => handleReplaceImage(i)}
                              className="p-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-sm"
                              title="Replace this image"
                            >
                              <FiUpload className="h-3 w-3" />
                            </button>
                            <button 
                              onClick={() => handleDeleteImage(i)}
                              className="p-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm"
                              title="Delete this image"
                            >
                              <FiTrash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div className="absolute top-0 left-0 bg-gray-900 bg-opacity-70 text-white text-xs px-1 rounded-sm">
                          {i === 0 ? 'Main' : `${i+1}`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500">No images available</div>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  Click "Add Multiple Images" to upload more product images. Hover over an image to replace or delete it.
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-800 flex justify-between">
              <button 
                onClick={closeEditModal}
                className="px-4 py-2 bg-background-light hover:bg-gray-800 rounded-md text-primary flex items-center"
              >
                <FiArrowLeft className="mr-2" />
                Cancel
              </button>
              <button 
                onClick={handleSaveProduct}
                className="px-4 py-2 bg-accent hover:bg-accent/90 rounded-md text-white flex items-center"
              >
                <FiSave className="mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleting && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-background border border-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="h-12 w-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiTrash2 className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-primary mb-2">Delete Product</h3>
              <p className="text-secondary mb-6">
                Are you sure you want to delete this product? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-background-light hover:bg-gray-800 rounded-md text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProduct}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-delete Confirmation Modal */}
      {isMultiDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-background border border-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="h-12 w-12 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiTrash2 className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-primary mb-2">Delete Multiple Products</h3>
              <p className="text-secondary mb-6">
                Are you sure you want to delete {selectedProducts.length} products? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setIsMultiDeleteModalOpen(false)}
                  className="px-4 py-2 bg-background-light hover:bg-gray-800 rounded-md text-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteMultipleProducts}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-white"
                >
                  Yes, Delete All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay />
    </div>
  );
};

export default Products; 